import React, { useEffect, useState } from "react";
import { LocalUser, RemoteUser } from "agora-rtc-react";
import { client } from "@/api"
import { BsFillMicMuteFill, BsFillMicFill } from "react-icons/bs";
import { IoArrowBack } from "react-icons/io5";
import { ImPhoneHangUp } from "react-icons/im";
import { FaPhoneAlt } from "react-icons/fa";
import { BsSpeaker, BsMic } from "react-icons/bs";
import { useNavigate, useLocation } from "react-router-dom";
import SessionTimer from "@/components/SessionTimer";
import { useInstantCall } from "@/hooks/use-instant-call";
import { getAppointments } from "@/api/services";
import { useAuth } from "@/hooks/use-auth";
import Generatedkundli from "@/components/Meeting/Generatedkundli";
import InCallChat from "@/components/InCallChat";
import toast from "react-hot-toast";

const UserAvatar = ({ name, className }) => (
  <div
    className={`${className} bg-yellow-100 rounded-full flex items-center justify-center overflow-hidden`}
  >
    <img
      src="/path/to/user-avatar.png"
      alt={name}
      className="h-full w-full object-cover"
      onError={(e) => {
        e.target.style.display = "none";
        e.target.parentElement.innerHTML = `
          <div class="h-full w-full flex items-center justify-center text-2xl font-semibold text-gray-500">
            ${name?.charAt(0) || "U"}
          </div>
        `;
      }}
    />
  </div>
);

export default function InstantVoiceCallPage() {
  const [callDuration, setCallDuration] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { astrologer } = useAuth();
  const [meetingId, setMeetingId] = useState(null);
  
  // Get navigation state
  const { channel, userSocketId, userId, userName } = location.state || {};
  
  // Add error boundary for voice call hook
  let hookResult;
  try {
    hookResult = useInstantCall("voice", channel);
  } catch (error) {
    console.error("useInstantCall hook failed:", error);
    toast.error("Failed to initialize voice call. Redirecting...");
    setTimeout(() => navigate("/"), 1000);
    return <div>Loading...</div>;
  }

  const {
    localMicrophoneTrack,
    localCameraTrack,
    micOn,
    remoteUsers,
    setIsUserReady,
    setMic,
    isConnected,
    isUserReady,
    microphoneDevices,
    handleMicrophoneDeviceChange,
    speakerDevices,
    handleSpeakerDeviceChange,
    cameraDevices,
    handleCameraDeviceChange,
  } = hookResult || {};

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUserReady(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [setIsUserReady]);

  const handleConnectionsClick = () => {
    if (isConnected) {
      setShowConfirmModal(true);
    } else {
      setIsUserReady(true);
    }
  };

  const handleEndCall = async () => {
    try {
      const response = await getAppointments(
        astrologer.astrologerId,
        astrologer.accessToken
      );

      const appointments = response.data?.appointments || [];
      if (appointments.length > 0) {
        const meetingId = appointments[0]._id;
        setMeetingId(meetingId);

        await client.put("/api/meeting/update-state", {
          meetingId: appointments[0]._id,
          newState: "completed",
          duration: callDuration,
        });
      } else {
        console.warn("No appointments found.");
      }
    } catch (error) {
      console.error("Error while ending call:", error);
    } finally {
      setIsUserReady(false);
      setShowConfirmModal(false);
      navigate("/connect/meeting-end")
    }
  };

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const extractDeviceName = (label) => {
    return label.replace(/\s*\(.*?\)$/, "");
  };

  const toggleSpeaker = async () => {
    setSpeakerOn(!speakerOn);
    if (speakerDevices && speakerDevices.length > 1) {
      const currentIdx = speakerDevices.findIndex(s => s.isActive);
      const nextIdx = (currentIdx + 1) % speakerDevices.length;
      const nextDevice = speakerDevices[nextIdx];
      if (nextDevice && nextDevice.deviceId) {
        await handleSpeakerDeviceChange({ target: { value: nextDevice.deviceId } });
      }
    }
  };

  return (
    <div className="flex flex-col w-full h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="md:hidden flex justify-between items-center p-4 bg-white border-b shadow-sm">
        <button
          onClick={() => {
            if (isConnected) {
              setShowConfirmModal(true);
            } else {
              navigate(-1);
            }
          }}
          className="flex items-center gap-2 px-3 py-2 bg-orange-400 text-white rounded-full text-sm"
        >
          <IoArrowBack size={16} />
          Back
        </button>
        <img
          className="h-8"
          src="/ApnaSanatan.svg"
          alt="apna-sanatan-logo"
        />
        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="px-3 py-2 bg-gray-100 rounded-lg text-sm"
        >
          {showSidebar ? 'Close' : 'Details'}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Call Interface */}
        <div className={`flex-1 flex flex-col ${showSidebar ? 'hidden md:flex' : 'flex'}`}>
          {/* Desktop Header */}
          <div className="hidden md:flex justify-between items-center border-b shadow-sm px-8 py-4 bg-white">
            <button
              onClick={() => {
                if (isConnected) {
                  setShowConfirmModal(true);
                } else {
                  navigate(-1);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-400 text-white rounded-full hover:bg-orange-500 transition-colors"
            >
              <IoArrowBack size={18} />
              Back
            </button>
            <img
              className="w-28 flex-shrink-0"
              src="/ApnaSanatan.svg"
              alt="apna-sanatan-logo"
            />
            <div className="w-20"></div> {/* Spacer for balance */}
          </div>

          {/* Video Container */}
          <div className="flex-1 relative bg-[#E1E1E1] m-4 md:m-7 rounded-xl">
            <div className="absolute top-4 right-4 z-30">
              <SessionTimer
                isConnected={isConnected}
                isUserReady={isUserReady}
                onDurationChange={setCallDuration}
              />
            </div>

            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              <UserAvatar
                name="User's Name"
                className="w-24 h-24 sm:w-32 sm:h-32 mb-4"
              />
              <div className="absolute bottom-4 left-4 text-base md:text-lg font-medium text-gray-800">
                User's Name
              </div>

              {/* Hidden Local User for audio functionality */}
              <div className="invisible">
                <LocalUser
                  playVideo={false}
                  playAudio={false}
                  audioTrack={localMicrophoneTrack}
                  micOn={micOn}
                  videoTrack={null}
                  cameraOn={false}
                />
              </div>

              {remoteUsers && (
                <div className={`absolute top-4 left-4 flex items-center ${remoteUsers.length > 0 ? 'text-green-500' : 'text-gray-500'}`}>
                  <div className={`w-3 h-3 rounded-full mr-2 ${remoteUsers.length > 0 ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <span className="text-xs sm:text-sm font-medium">
                    {remoteUsers.length > 0 ? 'Connected' : 'Waiting for user'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="p-4 md:py-4 md:px-6 bg-white md:bg-transparent">
            <div className="flex items-center justify-center relative">
              {/* Session Info - Hidden on mobile, shown on desktop */}
              <div className="hidden md:flex flex-col absolute left-4">
                <div className="text-lg font-semibold text-gray-800">Audio Call Consultation</div>
                <div className="text-sm text-gray-500">
                  {new Date().toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}|{" "}
                  {new Date().toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                  })}
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="flex flex-col items-center">
                  <button
                    onClick={toggleSpeaker}
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center ${speakerOn ? "bg-orange-100 text-orange-500" : "bg-gray-200 text-gray-500"
                      } shadow-md transition-colors`}
                  >
                    <BsSpeaker size={window.innerWidth < 640 ? 20 : 24} style={{ opacity: speakerOn ? 1 : 0.3 }} />
                  </button>
                  <span className="text-xs mt-1">Speaker</span>
                </div>

                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setMic((prev) => !prev)}
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center ${micOn ? "bg-orange-100 text-orange-500" : "bg-gray-200 text-gray-500"
                      } shadow-md transition-colors`}
                  >
                    <BsMic size={window.innerWidth < 640 ? 20 : 24} style={{ opacity: micOn ? 1 : 0.3 }} />
                  </button>
                  <span className="text-xs mt-1">Mic</span>
                </div>

                <div className="flex flex-col items-center">
                  <button
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    <ImPhoneHangUp size={window.innerWidth < 640 ? 20 : 24} />
                  </button>
                  <span className="text-xs mt-1">Leave Call</span>
                </div>
              </div>
            </div>

            {/* Mobile Session Info */}
            <div className="md:hidden mt-4 text-center">
              <div className="text-sm font-semibold text-gray-800">Audio Call Consultation</div>
              <div className="text-xs text-gray-500">
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}{" "}|{" "}
                {new Date().toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Hidden on mobile unless toggled */}
        <div className={`${showSidebar ? 'fixed inset-0 z-40 md:relative md:inset-auto' : 'hidden'
          } md:flex md:w-5/12 bg-white border-l border-gray-200 flex-col overflow-hidden rounded-none md:rounded-xl`}>
          {/* Mobile sidebar header */}
          <div className="md:hidden flex justify-between items-center p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">Session Details</h3>
            <button
              onClick={() => setShowSidebar(false)}
              className="px-3 py-1 bg-gray-200 rounded text-sm"
            >
              Close
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            <Generatedkundli />
            {/* Chat section */}
            <div className="p-4 border-t">
              <h3 className="font-semibold text-gray-800 mb-2">Chat</h3>
              {userId ? (
                <InCallChat
                  recipientId={`user_${userId}`}
                  recipientName={userName}
                />
              ) : (
                <p className="text-gray-500 text-sm">Chat unavailable</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleEndCall}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}

export const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            End Session
          </h3>
          <p className="text-gray-600 mb-6">
            Do you want to complete the session?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button
              onClick={onCancel}
              className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="w-full sm:w-auto px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              End Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};